import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useRazorpay } from 'react-razorpay';

import * as subscriptionApis from 'apis/subscriptions';
import PlansListStatic from '../common/PlansListStatic';
import logo from '../../assets/images/logo.png';

/*
  Renewal for a member whose subscription has lapsed.

  They cannot sign in, so there is no session to pay with. POST /v1/auth/login
  answers an expired-but-correct password with a renewalToken, and this page
  spends it on POST /v1/subscriptions and /verify-payment, the same two
  unauthenticated endpoints signup uses. Verification returns a session, so a
  completed payment lands the member straight on the dashboard.

  A lapsed subscription has no live mandate to edit, so renewal always buys a
  new one. That is true for card and UPI alike, which is why there is no
  card/UPI split here like there is on change-plan. UPI mandates are approved
  in the member's UPI app and can take a while to confirm, hence the capped
  verification poll.

  Signup promos do not apply to a renewal, so no promo field.
*/

const VERIFY_MAX_ATTEMPTS = 15;
const VERIFY_INTERVAL_MS = 2000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

function RenewPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { Razorpay } = useRazorpay();

  const renewalToken = state?.renewalToken;
  const email = state?.email;
  const expiredOn = formatDate(state?.expiredOn);

  const [status, setStatus] = useState('choosing');
  const [error, setError] = useState('');
  const [pendingPlan, setPendingPlan] = useState(null);

  // The token only lives in router state, so a direct visit or a refresh has
  // nothing to pay with. Send those back to sign in.
  if (!renewalToken) {
    return <Navigate to="/Login" replace />;
  }

  const finishVerification = async ({ paymentId, subscriptionId, signature }) => {
    for (let attempt = 0; attempt < VERIFY_MAX_ATTEMPTS; attempt += 1) {
      const { data } = await subscriptionApis.verify({
        paymentId,
        subscriptionId,
        signature,
        subscriptionToken: renewalToken,
      });

      if (!data?.wait) {
        return data?.session || null;
      }

      setStatus('confirming');
      await delay(VERIFY_INTERVAL_MS);
    }
    return null;
  };

  const openCheckout = ({ razorpayKey, subscriptionId, planId, prefill }) => {
    const razorpayInstance = new Razorpay({
      key: razorpayKey,
      name: 'The Taurian',
      description: 'Membership renewal',
      subscription_id: subscriptionId,
      prefill,
      theme: { color: '#F37254' },
      handler: async (response) => {
        setStatus('verifying');
        try {
          const session = await finishVerification({
            paymentId: response.razorpay_payment_id,
            subscriptionId: response.razorpay_subscription_id,
            signature: response.razorpay_signature,
          });

          if (!session) {
            setStatus('pending');
            return;
          }

          localStorage.setItem('token', session);
          localStorage.setItem('role', 'user');
          navigate('/dashboard');
        } catch (verifyError) {
          setStatus('pending');
          setError(
            verifyError?.response?.data?.error ||
              'We could not confirm the payment yet.',
          );
        }
      },
      modal: {
        ondismiss: () => {
          setStatus('choosing');
          setPendingPlan(null);
        },
      },
    });

    razorpayInstance.on('payment.failed', () => {
      setStatus('choosing');
      setPendingPlan(null);
      setError('That payment did not go through. Please try again.');
    });

    razorpayInstance.open();
    setPendingPlan(planId);
  };

  const handleSelectPlan = async (plan) => {
    setError('');
    setStatus('starting');
    try {
      const { data } = await subscriptionApis.create({
        planId: plan.id,
        subscriptionToken: renewalToken,
      });
      openCheckout({
        razorpayKey: data.razorpayKey,
        subscriptionId: data.subscriptionId,
        planId: plan.id,
        prefill: { email: data.email || email, contact: data.phone },
      });
    } catch (createError) {
      setStatus('choosing');
      setError(
        createError?.response?.data?.error ||
          'We could not start the renewal. Please try again.',
      );
    }
  };

  if (status === 'verifying' || status === 'confirming') {
    return (
      <RenewNotice
        title="Confirming your payment"
        body={
          status === 'confirming'
            ? 'Waiting for your bank to confirm the mandate. This can take a few moments for UPI.'
            : 'Just a moment.'
        }
      />
    );
  }

  if (status === 'pending') {
    return (
      <RenewNotice
        title="Payment received"
        body={
          error ||
          'Your mandate is still being confirmed. Once it clears you can sign in as usual.'
        }
        action={<Link to="/Login">Back to sign in</Link>}
      />
    );
  }

  const busy = status === 'starting' || pendingPlan !== null;

  return (
    <div className="App">
      <div className="renew-intro">
        <div className="logo">
          <img src={logo} alt="" />
        </div>
        <h1>{expiredOn ? 'Your membership has expired' : 'Choose a membership'}</h1>
        <p>
          {expiredOn
            ? `It ended on ${expiredOn}. Pick a plan below to get back in — your account and details stay as they are.`
            : 'Pick a plan below to activate your account.'}
        </p>
        {error && (
          <div className="errors">
            <h1>{error}</h1>
          </div>
        )}
      </div>

      <PlansListStatic
        showHero={false}
        showTrustStrip={false}
        showFreePeriod={false}
        onSelectPlan={handleSelectPlan}
        actionLabel={busy ? 'Please wait…' : 'Renew with this plan'}
        disabled={busy}
      />

      <div className="login-footer">
        <p>
          Changed your mind? <Link to="/Login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}

const RenewNotice = ({ title, body, action }) => (
  <div className="App">
    <div className="col-lg-100 col-md-100 col-sm-100 full-height pt-40">
      <div className="login pt-40">
        <div className="logo">
          <img src={logo} alt="" />
        </div>
        <div className="login-title">
          <h1>{title}</h1>
          <h6>{body}</h6>
        </div>
        {action && <div className="login-footer">{action}</div>}
      </div>
    </div>
  </div>
);

export default RenewPage;
